import React from "react";

export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto p-6 text-sm leading-6">
      <h1 className="text-2xl font-bold mb-4">Terms and Conditions</h1>
      <p className="text-xs opacity-70 mb-6">
        Last updated: {new Date().toISOString().slice(0, 10)}
      </p>

      <Section title="1. Acceptance of Terms">
        <p>
          By accessing or using the AstraTrade application and related services
          (the "AstraTrade App"), you agree to be bound by these Terms and our
          Privacy Policy. If you do not agree, do not use the AstraTrade App.
        </p>
      </Section>

      <Section title="2. Eligibility">
        <p>
          You must be at least the age of majority in your jurisdiction and
          legally capable of entering into a binding contract. You represent
          that all account information you provide is accurate and complete.
        </p>
      </Section>

      <Section title="3. Risk Disclosure">
        <ul className="list-disc pl-6">
          <li>
            Markets can be volatile; past performance does not guarantee future
            results.
          </li>
          <li>
            You may lose part or all of your capital. Only trade with money you
            can afford to lose.
          </li>
          <li>
            Slippage, latency, outages, and third‑party interruptions can impact
            prices and execution.
          </li>
        </ul>
      </Section>

      <Section title="4. Accounts and Security">
        <p>
          You are responsible for maintaining the confidentiality of your
          account credentials and for all activities that occur under your
          account. Notify us immediately of any unauthorized use or security
          breach. We may suspend or terminate accounts that we suspect are
          compromised, fraudulent, or violate these Terms.
        </p>
      </Section>

      <Section title="5. Funding, Withdrawals, and Balances">
        <p>
          Deposits and withdrawals may be subject to processing times, limits,
          verification requirements, and fees. Balances and statements may be
          delayed or based on external data and are not guaranteed to be
          real‑time.
        </p>
      </Section>

      <Section title="6. Orders, Positions, and Execution">
        <p>
          Orders are subject to market conditions and available liquidity;
          execution at a specific price or time is not guaranteed. Conditional
          orders (e.g., stop loss, take profit) may not execute at requested
          levels during high volatility.
        </p>
      </Section>

      <Section title="7. Fees and Taxes">
        <p>
          You are responsible for any trading fees, spreads, commissions, or
          third‑party charges disclosed in the App, and for determining and
          paying any applicable taxes.
        </p>
      </Section>

      <Section title="8. Market Data and Third‑Party Services">
        <p>
          Quotes, charts, and market data are provided "as is" and may be
          delayed, inaccurate, or incomplete. We are not responsible for
          interruptions or inaccuracies from third‑party providers.
        </p>
      </Section>

      <Section title="9. Prohibited Conduct">
        <p>
          Do not misuse the AstraTrade App, including: attempting unauthorized
          access; introducing malware; scraping; reverse engineering;
          interfering with or overloading systems; using the App for illegal
          activities; or violating applicable laws or regulations. We may
          suspend or terminate access and report unlawful activity.
        </p>
      </Section>

      <Section title="10. Compliance and KYC/AML">
        <p>
          We comply with applicable laws and regulations, including KYC/AML
          requirements where relevant. We may request identity documents and
          other information to verify users and assess risk.
        </p>
      </Section>

      <Section title="11. Intellectual Property">
        <p>
          The AstraTrade App, including its UI, code, features, content, and
          trademarks, is owned by us or our licensors. You receive a limited,
          non‑exclusive, non‑transferable license to use the AstraTrade App for
          personal or authorized business purposes.
        </p>
      </Section>

      <Section title="12. Privacy">
        <p>
          We process personal data in accordance with our Privacy Policy. By
          using the AstraTrade App, you consent to such processing.
        </p>
      </Section>

      <Section title="13. Disclaimers">
        <p>
          The AstraTrade App is provided on an "as is" and "as available" basis
          without warranties of any kind, express or implied, including
          merchantability, fitness for a particular purpose, and
          non‑infringement.
        </p>
      </Section>

      <Section title="14. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, we are not liable for
          indirect, incidental, special, consequential, exemplary, or punitive
          damages; loss of profits; loss of data; or business interruption, even
          if advised of the possibility of such damages.
        </p>
      </Section>

      <Section title="15. Indemnification">
        <p>
          You agree to indemnify and hold us harmless from any claims, damages,
          liabilities, costs, and expenses arising from your use of the
          AstraTrade App, your violation of these Terms, or infringement of
          third‑party rights.
        </p>
      </Section>

      <Section title="16. Changes to the App and Terms">
        <p>
          We may modify or discontinue features with or without notice. We may
          update these Terms at any time. Continued use after changes constitute
          acceptance.
        </p>
      </Section>

      <Section title="17. Suspension and Termination">
        <p>
          We may suspend or terminate your access for any reason, including
          breaches of these Terms or legal requirements. Upon termination, your
          right to use the App ceases immediately.
        </p>
      </Section>

      <Section title="18. Governing Law and Dispute Resolution">
        <p>
          These Terms are governed by the laws of your applicable jurisdiction,
          without regard to conflict of law provisions. Disputes shall be
          resolved exclusively in competent courts unless mandatory law provides
          otherwise.
        </p>
      </Section>

      <Section title="19. Auto‑trading & Bots">
        <p>
          Enabling automated strategies, algorithmic trading, or bots is at your
          sole risk. You are responsible for configuring position sizing, risk
          controls, and exit logic; monitoring performance; and disabling
          automation during abnormal market conditions or maintenance windows.
          Automated features can malfunction due to connectivity issues, data
          errors, or provider outages and may result in unintended orders or
          losses.
        </p>
        <p className="mt-2">Guidance on monitoring:</p>
        <ul className="list-disc pl-6">
          <li>
            Review open positions and P&amp;L regularly, especially after market
            opens/major news.
          </li>
          <li>
            Set sensible max drawdown/position limits and alerting where
            available.
          </li>
          <li>
            Keep contact details up‑to‑date to receive system notices or alerts.
          </li>
        </ul>
      </Section>

      <Section title="20. Contact">
        <p>
          For questions, support, or complaints:{" "}
          <a
            href="mailto:support@astratrade.io"
            className="text-blue-500 underline"
          >
            support@astratrade.io
          </a>
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-6">
      <h2 className="text-base font-semibold mb-2">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
